import { Box } from "@chakra-ui/react";
import {
  colors,
  customEvent,
  Dropdown,
  FUNERAL_SIDO_LIST,
  FUNERAL_SIGUNGU,
  Spacing,
} from "goi_common";
import { BaseButton, BaseStack } from "goi_common/src/design-system";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { 장례준비긴급도_LIST } from "@/containers/MatchContainer/constants/request";
import {
  Highlighter,
  RecipientDownArrow,
  RecipientFooter,
  RecipientSectionHeader,
} from "./RecipientUI";
import RecipientBottomSheet from "./RecipientBottomSheet";
import {
  RECIPIENT_BENEFIT,
  RECIPIENT_BENEFIT_ITEMS,
  RECIPIENT_COMPARE,
  RECIPIENT_COMPARE_BARS,
  RECIPIENT_FIXED_CTA,
  RECIPIENT_FORM,
  RECIPIENT_HERO,
  RECIPIENT_HIGHLIGHT,
  RECIPIENT_URGENCY_MAP,
} from "./recipientData";

const SIDO_LIST = FUNERAL_SIDO_LIST.map((sido) => ({ key: sido[1] as string, value: sido[0] }));
const FIXED_BAR_SPACE = 96; // 하단 fixed CTA 높이 + 여백

/**
 * 기초생활수급자 장례 혜택 랜딩(구 실험 #327 B, 상시 채택).
 * /match/request 진입(광고 "수급자" 키워드 OR ?template=beneficiary)에서 렌더링.
 * 헤더는 플랫폼 공용 헤더 사용. 폼 섹션 아래 RenewalFooter. 하단 fixed CTA는 폼으로 스크롤 + 폼 도달 시 숨김.
 */
export default function RecipientLandingContainer({
  setHideFooter,
}: {
  /** 랜딩은 자체 footer를 가지므로 MatchRequestContainer의 RenewalFooter를 숨긴다. */
  setHideFooter?: (hidden: boolean) => void;
}) {
  const [sido, setSido] = useState<number | null>(null);
  const [sigungu, setSigungu] = useState<number | null>(null);
  const [statusIndex, setStatusIndex] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [formReached, setFormReached] = useState(false); // 폼 도달 시 fixed CTA 숨김

  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHideFooter?.(true);
    return () => setHideFooter?.(false);
  }, [setHideFooter]);

  // 폼 섹션이 뷰에 들어오면 하단 fixed CTA를 자연스럽게 숨긴다.
  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setFormReached(entry.isIntersecting), {
      threshold: 0.15,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 스크롤 뎁스 25/50/75/100% 각 1회 발화.
  useEffect(() => {
    const fired = new Set<number>();
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const pct = (window.scrollY / total) * 100;
      ([25, 50, 75, 100] as const).forEach((m) => {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          customEvent({ newGtm: { path: "beneficiary", trigger_and_target: `scroll_${m}` } });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const sigunguValues = useMemo(() => {
    if (!sido) return [];
    return Array.from(FUNERAL_SIGUNGU.get(sido) ?? new Map()).map(([code, name]) => ({
      key: name as string,
      value: code as number,
    }));
  }, [sido]);

  const regionText = useMemo(() => {
    const sidoName = SIDO_LIST.find((s) => s.value === sido)?.key ?? "";
    const sigunguName = sigunguValues.find((s) => s.value === sigungu)?.key ?? "";
    return [sidoName, sigunguName].filter(Boolean).join(" ");
  }, [sido, sigungu, sigunguValues]);

  const statusText = statusIndex !== null ? 장례준비긴급도_LIST[statusIndex] : "";
  const status = statusText ? RECIPIENT_URGENCY_MAP[statusText] : undefined;

  // 지역(시/도·시/군/구) + 준비 상황 모두 입력해야 인라인 CTA 활성화.
  const formFilled = sido !== null && sigungu !== null && statusIndex !== null;

  const scrollToForm = () => {
    customEvent({ newGtm: { path: "beneficiary", trigger_and_target: "fixed_cta" } });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openSheet = () => {
    if (!formFilled) return;
    customEvent({ newGtm: { path: "beneficiary", trigger_and_target: "form_cta" } });
    setModalOpen(true);
  };

  return (
    <BaseStack
      width="100%"
      bg={colors.white}
      css={{ wordBreak: "keep-all" }}
      // fixed CTA가 보일 때만 하단 여백 확보. 폼 도달(CTA 숨김) 시 0 → footer 아래 빈 흰 공간 제거.
      pb={formReached ? "0px" : `${FIXED_BAR_SPACE}px`}
    >
      {/* Hero */}
      <Box px="20px" pt="22px" pb="20px">
        <Box
          as="h1"
          className="head_24_b"
          color={colors.gray900}
          pb="10px"
          borderBottom={`1.5px solid ${colors.gray900}`}
        >
          {RECIPIENT_HERO.title}
        </Box>
        <Spacing gap={10} />
        <Box className="body_18_m" color={colors.gray900} lineHeight={1.5}>
          받을 수 있는 <Highlighter>장례 혜택</Highlighter>과 <Highlighter>예상 비용</Highlighter>을
          <br />
          1분 만에 확인해보세요
        </Box>
      </Box>

      {/* 절감 하이라이트 박스 */}
      <Box px="20px" pb="20px">
        <Box
          bg={colors.orange100}
          borderRadius="14px"
          p="16px"
          className="body_16_m"
          lineHeight={1.6}
          textAlign="center"
        >
          {RECIPIENT_HIGHLIGHT.prefix}
          <Box as="span" color={colors.orange700} fontWeight={700}>
            {RECIPIENT_HIGHLIGHT.emphasis}
          </Box>
          {RECIPIENT_HIGHLIGHT.suffix}
        </Box>
      </Box>

      {/* 다운 애로우 */}
      <RecipientDownArrow />

      {/* 섹션 1 — 혜택 목록 */}
      <Box px="20px" pt="20px" pb="28px">
        <RecipientSectionHeader num={RECIPIENT_BENEFIT.num} title={RECIPIENT_BENEFIT.title} />
        <Spacing gap={12} />
        <BaseStack
          width="100%"
          bg={colors.gray50}
          borderRadius="16px"
          p="16px"
          pl="20px"
          spacing={14}
        >
          {RECIPIENT_BENEFIT_ITEMS.map((item, i) => (
            <Fragment key={item.title}>
              {i > 0 && <Box height="1px" bg={colors.gray200} />}
              <BaseStack direction="horizontal" spacing={10} alignItems="flex-start">
                <Box
                  width="6px"
                  height="6px"
                  borderRadius="50%"
                  bg={colors.orange650}
                  flexShrink={0}
                  mt="8px"
                />
                <BaseStack flex={1} spacing={item.desc ? 4 : 0}>
                  <Box className="subtitle2_b" color={colors.gray900}>
                    {item.title}
                  </Box>
                  {item.desc && (
                    <Box className="body_15_r" color={colors.gray600} lineHeight={1.55}>
                      {item.desc}
                    </Box>
                  )}
                </BaseStack>
              </BaseStack>
            </Fragment>
          ))}
        </BaseStack>
      </Box>

      {/* 섹션 2 — 무빈소 비용 비교 */}
      <Box px="20px" pt="16px" pb="40px">
        <RecipientSectionHeader num={RECIPIENT_COMPARE.num} title={RECIPIENT_COMPARE.title} />
        <Spacing gap={12} />
        <Box className="body_16_r" color={colors.gray700} lineHeight={1.6}>
          {RECIPIENT_COMPARE.lead.prefix}
          <Highlighter>{RECIPIENT_COMPARE.lead.emphasis}</Highlighter>
          {RECIPIENT_COMPARE.lead.suffix}
        </Box>

        <Spacing gap={12} />
        {/* 막대 그래프 카드 */}
        <Box
          bg={colors.orange50}
          border={`1px solid ${colors.orange200}`}
          borderRadius="14px"
          p="16px 16px 14px"
        >
          <BaseStack direction="horizontal" spacing={20} alignItems="flex-end" height="190px">
            {RECIPIENT_COMPARE_BARS.map((bar) => (
              <BaseStack
                key={bar.key}
                flex={1}
                height="100%"
                spacing={8}
                alignItems="center"
                justifyContent="flex-end"
              >
                <Box
                  className="body_15_sb"
                  color={bar.isPrimary ? colors.orange700 : colors.gray700}
                >
                  {bar.priceLabel}
                </Box>
                <Box
                  width="100%"
                  height={`${bar.heightPct}%`}
                  borderRadius="8px 8px 0 0"
                  bg={bar.isPrimary ? colors.orange650 : colors.gray300}
                />
                <Box className="body_15_sb" color={colors.gray900}>
                  {bar.bottomLabel}
                </Box>
              </BaseStack>
            ))}
          </BaseStack>
        </Box>
        <Spacing gap={12} />
        <Box className="body_14_r" color={colors.gray700}>
          {RECIPIENT_COMPARE.caption}
        </Box>

        <Spacing gap={12} />
        {/* 무빈소란? 설명 박스 */}
        <BaseStack width="100%" bg={colors.gray50} borderRadius="12px" p="14px" spacing={6}>
          <Box className="subtitle2_b" color={colors.gray900}>
            {RECIPIENT_COMPARE.defTitle}
          </Box>
          <Box className="body_14_r" color={colors.gray700} whiteSpace="pre-line" lineHeight={1.65}>
            {RECIPIENT_COMPARE.defBody}
          </Box>
        </BaseStack>
      </Box>

      {/* 폼 섹션 (fixed CTA 스크롤 타겟) — 진한 배경 + 흰 카드 (후불제 seg 폼과 동일 톤).
          scrollMarginTop: fixed CTA 스크롤 시 플랫폼 헤더에 타이틀이 가려지지 않게 오프셋. */}
      <Box ref={formRef} bg={colors.gray600} p="24px 20px" style={{ scrollMarginTop: "76px" }}>
        <Box bg={colors.white} borderRadius="16px" p="24px">
          <Box as="h2" className="subtitle_21_b" color={colors.gray900}>
            {RECIPIENT_FORM.title}
          </Box>
          <Spacing gap={8} />
          <Box className="body_15_r" color={colors.gray700} lineHeight={1.6}>
            {RECIPIENT_FORM.subtext.prefix}
            <Box as="span" textDecoration="underline">
              {RECIPIENT_FORM.subtext.underline}
            </Box>
            {RECIPIENT_FORM.subtext.suffix}
          </Box>

          {/* 지역 */}
          <Spacing gap={18} />
          <Box className="body_15_sb" color={colors.gray900} fontWeight={700}>
            {RECIPIENT_FORM.regionLabel}
          </Box>
          <Spacing gap={8} />
          <BaseStack width="100%" direction="horizontal" spacing={8}>
            <Box flex={1}>
              <Dropdown
                name="시/도"
                width="100%"
                forceZIndex={1}
                optionsZIndex={999}
                value={sido ?? undefined}
                values={SIDO_LIST}
                onChange={(v) => {
                  setSido((v as number) ?? null);
                  setSigungu(null);
                  customEvent({
                    newGtm: { path: "beneficiary", trigger_and_target: "region-answer" },
                  });
                }}
              />
            </Box>
            <Box flex={1}>
              <Dropdown
                name="시/군/구"
                width="100%"
                forceZIndex={1}
                optionsZIndex={999}
                value={sigungu ?? undefined}
                values={sigunguValues}
                onChange={(v) => setSigungu((v as number) ?? null)}
              />
            </Box>
          </BaseStack>

          {/* 준비 상황 */}
          <Spacing gap={18} />
          <Box className="body_15_sb" color={colors.gray900} fontWeight={700}>
            {RECIPIENT_FORM.statusLabel}
          </Box>
          <Spacing gap={8} />
          <BaseStack width="100%" spacing={8}>
            {장례준비긴급도_LIST.map((label, i) => {
              const selected = statusIndex === i;
              return (
                <Box
                  key={label}
                  as="button"
                  width="100%"
                  p="13px 12px"
                  borderRadius="10px"
                  bg={selected ? colors.orange50 : colors.white}
                  border={`1.5px solid ${selected ? colors.orange650 : colors.gray200}`}
                  className="body_15_r"
                  color={selected ? colors.orange700 : colors.gray900}
                  fontWeight={selected ? 700 : undefined}
                  textAlign="center"
                  style={{ transition: "all 200ms cubic-bezier(0.22,1,0.36,1)" }}
                  onClick={() => {
                    setStatusIndex(i);
                    customEvent({
                      newGtm: { path: "beneficiary", trigger_and_target: "status-answer" },
                    });
                  }}
                >
                  {label}
                </Box>
              );
            })}
          </BaseStack>

          {/* 인라인 CTA → 바텀시트 */}
          <Spacing gap={24} />
          <Box width="100%">
            <BaseButton type="secondary" size="large" disabled={!formFilled} onClick={openSheet}>
              <Box className="body_16_m" fontWeight={700}>
                {RECIPIENT_FORM.inlineCta}
              </Box>
            </BaseButton>
          </Box>
          <Spacing gap={8} />
          <Box className="caption_13_r" color={colors.gray500} textAlign="center">
            {RECIPIENT_FORM.inlineCtaSub}
          </Box>
        </Box>
      </Box>

      {/* footer — 짧은 컴팩트 footer (RenewalFooter가 길어 대체) */}
      <RecipientFooter />

      {/* 하단 fixed CTA — 폼으로 스크롤, 폼 도달 시 숨김 */}
      <Box
        position="fixed"
        bottom="0"
        left="50%"
        transform="translateX(-50%)"
        width="100%"
        maxWidth="480px"
        bg={colors.white}
        borderTop={`1px solid ${colors.gray100}`}
        padding="10px 20px 16px"
        zIndex={100}
        style={{
          opacity: formReached ? 0 : 1,
          transform: formReached ? "translate(-50%, 100%)" : "translate(-50%, 0)",
          transition:
            "opacity 200ms cubic-bezier(0.22,1,0.36,1), transform 200ms cubic-bezier(0.22,1,0.36,1)",
          pointerEvents: formReached ? "none" : "auto",
        }}
      >
        <Box width="100%">
          <BaseButton type="primary" size="large" onClick={scrollToForm}>
            <Box className="body_16_m" fontWeight={700}>
              {RECIPIENT_FIXED_CTA}
            </Box>
          </BaseButton>
        </Box>
      </Box>

      <RecipientBottomSheet
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        regionText={regionText}
        statusText={statusText}
        status={status}
      />
    </BaseStack>
  );
}
